import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { isEmpty } from "ramda";
import { getPhoneNumbers } from "./selector";
import loadable from "@loadable/component";
const CancelSellOrderModal = loadable(() =>
  import(
    /* webpackChunkName: "CancelSellOrderModal" */ "../../components/CancelSellOrderModal"
  )
);
import { cancelSellOrder } from "@actions/sellActions";
import { defaultAction } from "@actions/defaultAction";
import {
  HIDE_LOADING,
  SET_SELL_ORDER_TO_CANCEL,
  OPEN_CANCEL_SELL_ORDER_MODAL,
  RESET_SELL,
  SHOW_TWILIO_POPUP
} from "@constants/actionTypes";
import NotifyButtons from "./NotifyButtons";
import PhoneNumberPicker from "./PhoneNumberPicker";
import { ClearedStyleRouterLink } from "@modules/Components";
import { registeringEvent, fireEvent } from "../Emission/action";
import { useHistory } from "react-router-dom";

const MissingContainer = styled.div`
  text-align: center;
  color: #777984;
  padding-top: 25px;
`;

const MissingHeader = styled.div`
  font-size: 22px;
  margin-bottom: 20px;
  font-weight: 500;
`;

const MissingP = styled.div`
  font-size: 17px;
  margin-bottom: 10px;
`;

const MissingSpan = styled.span`
  cursor: pointer;
  color: #1f2021;
  font-size: 18px;
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  display: block;
  padding-bottom: 40px;
`;

const Header = styled.div`
  font-size: 30px;
  font-weight: 500;
  text-align: center;
  color: #1f2021;
`;

const VerifyHeader = styled(Header)`
  @media (max-width: 525px) {
    font-size: 27px;
  }
  @media (max-width: 370px) {
    font-size: 25px;
  }
  @media (max-width: 335px) {
    font-size: 21px;
  }
`;

const Text = styled.div`
  font-size: 16px;
  line-height: 1.75;
  text-align: center;
  color: #777984;
  max-width: 455px;
  margin: 0 auto;
  padding-top: 18px;
  padding-bottom: 35px;

  @media (max-width: 525px) {
    font-size: 15px;
  }
`;

const OtherOptions = styled.div`
  font-size: 16px;
  text-align: center;
  color: #1f2021;
  margin-top: 30px;
`;

const FinishLater = styled.span`
  cursor: pointer;
`;

const Link = styled(ClearedStyleRouterLink)`
  color: #1f2021;
`;

const OtherOptionsLight = styled.span`
  color: #777984;
`;

const CancelOrder = styled.span`
  cursor: pointer;
`;

const PhoneMissing = styled.div`
  font-size: 14px;
  line-height: 1.71;
  text-align: center;
  color: #777984;
  border-top: 1px solid rgba(119, 121, 132, 0.15);
  margin-top: 30px;
  padding-top: 30px;
`;

const MissingButton = styled.span`
  color: #1f2021;
  cursor: pointer;
`;

const SetupVerificationMethod = ({ type, orderUUID, closable, page }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const phoneNumbers = useSelector(state =>
    getPhoneNumbers({
      phoneNumbers: state.orderVerification.phoneNumbers
    })
  );
  const sellOrderToCancel = useSelector(
    state => state.ordersSell.sellOrderToCancel
  );

  const [displayMissingMessage, changeDisplayMissingMessage] = useState(false);
  if (isEmpty(phoneNumbers)) return null;
  return (
    <div className="setup-verification-method">
      <CancelSellOrderModal
        cancelSellOrder={() => dispatch(cancelSellOrder(sellOrderToCancel))}
        sellOrderToCancel={sellOrderToCancel}
        onCancelAction={() => {
          history.push("/");
          dispatch(defaultAction(HIDE_LOADING));
          dispatch(defaultAction(RESET_SELL));
          dispatch(defaultAction(SHOW_TWILIO_POPUP, false));
        }}
      />
      {displayMissingMessage ? (
        <MissingContainer>
          <MissingHeader>Sorry</MissingHeader>
          <MissingP>
            CardCash's security tools are very sensitive and your phone number
            is needed to verify your identity. Unfortunately we can only
            authenticate your identity with a phone number provided by our fraud
            service.
          </MissingP>
          <div>
            <MissingSpan onClick={() => changeDisplayMissingMessage(false)}>
              Back
            </MissingSpan>
          </div>
        </MissingContainer>
      ) : (
        <>
          <VerifyHeader>
            {page ? "Phone verification" : "Additional verification required"}
          </VerifyHeader>
          <Text>
            Please select from one of the following phone numbers where you can
            receive a text message or phone call.
          </Text>

          <PhoneNumberPicker />

          <NotifyButtons type={type} orderUUID={orderUUID} />
          {!closable && !page && (
            <OtherOptions>
              <FinishLater>
                <Link
                  to="/account/sell/"
                  onClick={() => {
                    dispatch(defaultAction(HIDE_LOADING));
                    dispatch(defaultAction(RESET_SELL));
                    dispatch(defaultAction(SHOW_TWILIO_POPUP, false));
                  }}
                >
                  Finish Later
                </Link>
              </FinishLater>
              <OtherOptionsLight>&nbsp;or&nbsp;</OtherOptionsLight>
              <CancelOrder
                onClick={() => {
                  dispatch(defaultAction(SET_SELL_ORDER_TO_CANCEL, orderUUID));
                  dispatch(defaultAction(OPEN_CANCEL_SELL_ORDER_MODAL, true));
                }}
              >
                Cancel Order
              </CancelOrder>
            </OtherOptions>
          )}
          <PhoneMissing>
            Phone number missing? We’re sorry we cannot complete the order,
            <MissingButton
              onClick={() => {
                changeDisplayMissingMessage(true);
                dispatch(registeringEvent("PV_CANT_FIND_NUMBER_CLICKED"));

                dispatch(fireEvent("PV_CANT_FIND_NUMBER_CLICKED"));
              }}
            >
              {" "}
              click here to learn why
            </MissingButton>
          </PhoneMissing>
        </>
      )}
    </div>
  );
};

export default SetupVerificationMethod;
